import { useEffect, useRef } from 'react';
import { MarkerClusterer, SuperClusterAlgorithm, Renderer } from '@googlemaps/markerclusterer';

// Company pins for the project map.
//
// Offices pinned from a postcode or town centre share exact coordinates —
// every "Chicago" office without a postcode lands on one point — so pins
// at the same spot become ONE pin with a count, and clicking it lists the
// companies there (onGroupClick). Zoomed out, nearby pins merge into
// numbered clusters that split as you zoom in. Markers are created once per
// set of companies; selection / visited state only restyles the pins that
// change, so big projects stay responsive.

export interface PinCompany {
  id: number;
  company_name: string;
  latitude: number | string | null;
  longitude: number | string | null;
  pin_color?: string | null;
}

const NEUTRAL = '#6B7280';
const keyOf = (c: PinCompany) => `${Number(c.latitude).toFixed(5)},${Number(c.longitude).toFixed(5)}`;

const pinSvg = (color: string, state: 'normal' | 'visited' | 'selected') => {
  if (state === 'selected') {
    return `<svg width="36" height="48" viewBox="0 0 36 48" xmlns="http://www.w3.org/2000/svg">
      <circle cx="18" cy="18" r="18" fill="rgba(66, 133, 244, 0.2)" stroke="rgba(66, 133, 244, 0.4)" stroke-width="2"/>
      <path d="M18 4C11.4 4 6 9.4 6 16c0 6.6 12 20 12 20s12-13.4 12-20c0-6.6-5.4-12-12-12z" fill="${color}" stroke="#FFFFFF" stroke-width="4"/>
      <circle cx="18" cy="16" r="8" fill="#FFFFFF"/><circle cx="18" cy="16" r="5" fill="${color}"/></svg>`;
  }
  return `<svg width="24" height="32" viewBox="0 0 24 32" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 0C5.4 0 0 5.4 0 12c0 6.6 12 20 12 20s12-13.4 12-20c0-6.6-5.4-12-12-12z" fill="${color}"
      stroke="${state === 'visited' ? '#8B5CF6' : '#FFFFFF'}" stroke-width="${state === 'visited' ? 3 : 2}" ${state === 'visited' ? 'opacity="0.85"' : ''}/>
    <circle cx="12" cy="12" r="6" fill="#FFFFFF"/><circle cx="12" cy="12" r="3" fill="${color}"/></svg>`;
};

// A same-spot group: a pin with the number of companies on it.
const groupSvg = (color: string, n: number, selected: boolean) => {
  const label = n > 999 ? '999+' : String(n);
  const w = 24 + Math.max(0, label.length - 1) * 6;
  return `<svg width="${w + 8}" height="40" viewBox="0 0 ${w + 8} 40" xmlns="http://www.w3.org/2000/svg">
    <path d="M${(w + 8) / 2} 39 l-6 -9 h12 z" fill="${color}"/>
    <rect x="1" y="1" width="${w + 6}" height="28" rx="14" fill="${color}" stroke="${selected ? '#1D4ED8' : '#FFFFFF'}" stroke-width="${selected ? 3 : 2}"/>
    <text x="${(w + 8) / 2}" y="20" text-anchor="middle" font-family="Arial, sans-serif" font-size="13" font-weight="700" fill="#FFFFFF">${label}</text></svg>`;
};

const icon = (svg: string, anchorX: number, anchorY: number): google.maps.Icon => ({
  url: 'data:image/svg+xml,' + encodeURIComponent(svg),
  anchor: new google.maps.Point(anchorX, anchorY),
});

// Clusters show how many COMPANIES they hold (a group pin counts as its size).
const renderer: Renderer = {
  render: ({ markers, position }) => {
    const n = (markers || []).reduce((s, m: any) => s + (m.__companies || 1), 0);
    const size = n >= 1000 ? 56 : n >= 100 ? 48 : 40;
    const label = n >= 10000 ? `${Math.round(n / 1000)}k` : String(n);
    const svg = `<svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" xmlns="http://www.w3.org/2000/svg">
      <circle cx="${size / 2}" cy="${size / 2}" r="${size / 2 - 2}" fill="#2563EB" fill-opacity="0.85" stroke="#FFFFFF" stroke-width="3"/>
      <text x="${size / 2}" y="${size / 2 + 5}" text-anchor="middle" font-family="Arial, sans-serif" font-size="14" font-weight="700" fill="#FFFFFF">${label}</text></svg>`;
    return new google.maps.Marker({
      position,
      icon: icon(svg, size / 2, size / 2),
      zIndex: 1000 + n,
      title: `${n.toLocaleString()} companies`,
    });
  },
};

export function CompanyPinLayer({ map, companies, selectedId, visitedIds, onSelect, onGroupClick }: {
  map: google.maps.Map | null;
  companies: PinCompany[];
  selectedId: number | null;
  visitedIds: Set<number>;
  onSelect: (c: PinCompany) => void;
  onGroupClick: (companies: PinCompany[], position: { lat: number; lng: number }) => void;
}) {
  const clustererRef = useRef<MarkerClusterer | null>(null);
  const markersRef = useRef<Map<string, { marker: google.maps.Marker; group: PinCompany[]; color: string }>>(new Map());
  // Latest callbacks / state without rebuilding markers when they change.
  const live = useRef({ onSelect, onGroupClick, selectedId, visitedIds });
  live.current = { onSelect, onGroupClick, selectedId, visitedIds };

  const styleOf = (group: PinCompany[], color: string): { icon: google.maps.Icon; zIndex: number } => {
    const { selectedId: sel, visitedIds: seen } = live.current;
    const selected = group.some((c) => c.id === sel);
    if (group.length > 1) {
      const svg = groupSvg(color, group.length, selected);
      const w = 32 + Math.max(0, String(group.length).length - 1) * 6;
      return { icon: icon(svg, w / 2, 39), zIndex: selected ? 1000 : 10 };
    }
    const state = selected ? 'selected' : seen.has(group[0].id) ? 'visited' : 'normal';
    return { icon: state === 'selected' ? icon(pinSvg(color, state), 18, 36) : icon(pinSvg(color, state), 12, 32), zIndex: selected ? 1000 : 1 };
  };

  // Build markers whenever the set of companies on the map changes.
  useEffect(() => {
    if (!map) return undefined;
    const groups = new Map<string, PinCompany[]>();
    for (const c of companies) {
      if (c.latitude == null || c.longitude == null || c.latitude === '' || c.longitude === '') continue;
      const lat = Number(c.latitude), lng = Number(c.longitude);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
      const k = keyOf(c);
      (groups.get(k) || groups.set(k, []).get(k)!).push(c);
    }
    const entries = new Map<string, { marker: google.maps.Marker; group: PinCompany[]; color: string }>();
    const markers: google.maps.Marker[] = [];
    groups.forEach((group, k) => {
      const colors = new Set(group.map((c) => c.pin_color || '#3B82F6'));
      const color = colors.size === 1 ? Array.from(colors)[0] : NEUTRAL;
      const position = { lat: Number(group[0].latitude), lng: Number(group[0].longitude) };
      const marker = new google.maps.Marker({
        position,
        title: group.length > 1 ? `${group.length} companies here` : group[0].company_name,
        ...styleOf(group, color),
      });
      (marker as any).__companies = group.length;
      marker.addListener('click', () => {
        if (group.length > 1) live.current.onGroupClick(group, position);
        else live.current.onSelect(group[0]);
      });
      entries.set(k, { marker, group, color });
      markers.push(marker);
    });
    markersRef.current = entries;
    clustererRef.current = new MarkerClusterer({
      map,
      markers,
      renderer,
      // Stop clustering once zoomed into a neighbourhood; same-spot groups
      // stay as count pins below that.
      algorithm: new SuperClusterAlgorithm({ maxZoom: 14, radius: 60 }),
    });
    return () => {
      clustererRef.current?.clearMarkers();
      clustererRef.current?.setMap(null);
      clustererRef.current = null;
      entries.forEach((e) => google.maps.event.clearInstanceListeners(e.marker));
      markersRef.current = new Map();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, companies]);

  // Restyle only on selection / visited changes.
  useEffect(() => {
    markersRef.current.forEach(({ marker, group, color }) => {
      const s = styleOf(group, color);
      marker.setIcon(s.icon);
      marker.setZIndex(s.zIndex);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, visitedIds]);

  return null;
}
